import * as Zod from 'zod';

import { translate } from 'utils/i18n';
import { fileValidator } from 'common/validators/file';
import { certificateValidator } from 'common/validators/certificate';
import { groupConditionValidator } from 'user/validators/groupCondition';
import { IOfferEventType } from 'graphql/types';
import { extraCategoryInputValidator } from 'common/validators/extraCategory';
import { dateTimeValidator } from 'common/validators/dateTime';
import { offerEventDateValidator } from 'offer/validators/offerEventDate';
import { IGroupConditionInput } from 'user/types';

function getAuthorValidation(
    currentUserIsAuthor: boolean,
    data: IGroupConditionInput[]
) {
    if (!currentUserIsAuthor) return true;

    // Should contain at least one group condition when author
    return !!data?.length;
}

export function getOfferEventFormValidator(currentUserIsAuthor: boolean) {
    return Zod.object({
        title: Zod.string().optional(),
        offerEventTitle: Zod.string().optional(),
        description: Zod.string().optional(),
        groupConditions: Zod.array(groupConditionValidator)
            .refine(
                // If current user is author than atleast one group condition is required
                (data: IGroupConditionInput[]) =>
                    getAuthorValidation(currentUserIsAuthor, data),
                {
                    message: translate('validate.required'),
                    path: ['groupConditions'],
                }
            )
            .optional(),
        image: fileValidator.optional().nullable(),
        overviewImage: fileValidator.optional().nullable(),
        titleColor: Zod.string().nullable().optional(),
        hasDetailPage: Zod.boolean().optional(),
        publish: Zod.boolean().optional(),
        certificate: certificateValidator.optional().nullable(),
        trainers: Zod.array(Zod.string()).optional(),
        trainerApproval: Zod.boolean().optional(),
        minSubscriptions: Zod.coerce
            .number<number>()
            .int()
            .nonnegative()
            .optional(),
        maxSubscriptions: Zod.coerce
            .number<number>()
            .int()
            .nonnegative()
            .optional(),
        waitingList: Zod.boolean().optional(),
        groupManagersCanEnrollParticipants: Zod.boolean().optional(),
        descriptionBeforeSubscription: Zod.string().optional(),
        descriptionAfterSubscription: Zod.string().optional(),
        accessTrainings: Zod.array(Zod.string()).optional(),
        accessExternalContents: Zod.array(Zod.string()).optional(),
        accessCourses: Zod.array(Zod.string()).optional(),
        showParticipantList: Zod.boolean().optional(),
        type: Zod.enum(IOfferEventType),
        accessScorms: Zod.array(Zod.string()).optional(),
        accessVideos: Zod.array(Zod.string()).optional(),
        scoreThreshold: Zod.number().optional(),
        extraCategories: extraCategoryInputValidator(true).optional(),
        isServiceProviderWebinar: Zod.boolean().optional(),
        dates: Zod.array(offerEventDateValidator).optional(),
        openingDate: dateTimeValidator.optional().nullable(),
        closingDate: dateTimeValidator.optional().nullable(),
        unenrollClosingDate: dateTimeValidator.optional().nullable(),
        publicParticipantList: Zod.boolean().optional(),
    })
        .refine(
            // At least one of title or offerEventTitle must be provided
            (data) => !!data.title || !!data.offerEventTitle,
            {
                message: translate('validate.required'),
                path: ['title'],
            }
        )
        .refine(
            // At least one of title or offerEventTitle must be provided
            (data) => !!data.title || !!data.offerEventTitle,
            {
                message: translate('validate.required'),
                path: ['offerEventTitle'],
            }
        )
        .refine(
            // minSubscriptions must be less than or equal to maxSubscriptions
            (data) =>
                !data.minSubscriptions ||
                !data.maxSubscriptions ||
                (data.minSubscriptions &&
                    data.maxSubscriptions &&
                    !!data.maxSubscriptions &&
                    data.minSubscriptions <= data.maxSubscriptions),
            {
                message: translate('validate.lessOrEqual'),
                path: ['minSubscriptions'],
            }
        )
        .refine(
            // maxSubscriptions must be greater than or equal to minSubscriptions
            (data) =>
                !data.maxSubscriptions ||
                (!!data.maxSubscriptions &&
                    data.maxSubscriptions >= (data?.minSubscriptions ?? 0)),
            {
                message: translate('validate.moreOrEqual'),
                path: ['maxSubscriptions'],
            }
        )
        .refine(
            // openingDate must be before closingDate (when both are set)
            (data) =>
                !data.openingDate ||
                !data.closingDate ||
                data.openingDate.toMillis() < data.closingDate.toMillis(),
            {
                message: translate('validate.openingDateBeforeClosingDate'),
                path: ['openingDate'],
            }
        )
        .refine(
            // unenrollClosingDate must not be before openingDate (when both are
            // set). It may be set before or after the closingDate.
            (data) =>
                !data.openingDate ||
                !data.unenrollClosingDate ||
                data.openingDate.toMillis() <
                    data.unenrollClosingDate.toMillis(),
            {
                message: translate(
                    'validate.openingDateBeforeUnenrollClosingDate'
                ),
                path: ['unenrollClosingDate'],
            }
        );
}
